import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { groupByDay } from "@/components/admin-portal/routing-mode/history-diff";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type {
	IncentiveLedgerEntry,
	LedgerEntryType,
} from "@/types/admin-portal/bonus-ro";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import { DEFAULT_LIMIT, formatSigned, ledgerActor } from "./balances";
import { LoadFailed, Loading } from "./load-state";

const timeFormat = new Intl.DateTimeFormat("id-ID", {
	timeZone: "Asia/Jakarta",
	hour: "2-digit",
	minute: "2-digit",
});

// groupByDay keys are YYYY-MM-DD in WIB; read back as a local date for the heading.
const dayLabel = (day: string) =>
	new Date(`${day}T00:00:00`).toLocaleDateString("id-ID", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});

const DOT: Record<LedgerEntryType, string> = {
	EARN: "bg-emerald-500",
	REVERSAL: "bg-destructive",
	ADJUSTMENT: "bg-amber-500",
	PAYOUT: "bg-primary",
};

/** One therapist's ledger as a day-grouped feed, newest first, a page at a time. */
export function LedgerDrawer({
	open,
	therapistName,
	ledger,
	onClose,
	onPage,
}: {
	open: boolean;
	therapistName?: string;
	/** undefined or null while the first page loads */
	ledger?: ExternalProp<IncentiveLedgerEntry[]> | null;
	onClose: () => void;
	onPage: (page: number, limit: number) => void;
}) {
	const { t } = useTranslation("bonus-ro");

	return (
		<Sheet open={open} onOpenChange={(next) => !next && onClose()}>
			<SheetContent className="flex w-full flex-col gap-6 overflow-y-auto pb-0 sm:max-w-3xl">
				<SheetHeader className="pr-6">
					<SheetTitle>
						{therapistName
							? t("ledger.title_for", { name: therapistName })
							: t("ledger.title")}
					</SheetTitle>
					<SheetDescription>{t("ledger.description")}</SheetDescription>
				</SheetHeader>
				{!ledger ? (
					<Loading />
				) : ledger.error ? (
					<LoadFailed
						title={t("failed.ledger")}
						error={ledger.error}
						only="ledger"
					/>
				) : (
					<LedgerFeed ledger={ledger} onPage={onPage} />
				)}
			</SheetContent>
		</Sheet>
	);
}

function LedgerFeed({
	ledger,
	onPage,
}: {
	ledger: ExternalProp<IncentiveLedgerEntry[]>;
	onPage: (page: number, limit: number) => void;
}) {
	const { t } = useTranslation("bonus-ro");
	const rows = ledger.data ?? [];
	const page = ledger.meta?.page ?? 1;
	const limit = ledger.meta?.limit ?? DEFAULT_LIMIT;
	const pages = ledger.meta?.totalPages ?? 1;
	const from = (page - 1) * limit + 1;

	if (rows.length === 0) {
		return (
			<div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
				{t("ledger.empty")}
			</div>
		);
	}

	return (
		<>
			<ol className="flex flex-1 flex-col gap-6">
				{groupByDay(rows, (entry) => entry.createdAt).map((group) => (
					<li key={group.day}>
						<h3 className="mb-3 flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							{dayLabel(group.day)}
							<span aria-hidden className="h-px flex-1 bg-border" />
						</h3>
						<ol>
							{group.rows.map((entry, i) => (
								<Entry
									key={entry.id}
									entry={entry}
									last={i === group.rows.length - 1}
								/>
							))}
						</ol>
					</li>
				))}
			</ol>
			{/* Pinned to the drawer's bottom edge so a long page never hides its pager. */}
			<div className="sticky bottom-0 -mx-6 mt-auto border-t bg-background px-6 py-4">
				<ApptPagination
					metadata={{
						page,
						pages,
						limit,
						count: ledger.meta?.totalItems ?? rows.length,
						from,
						to: from + rows.length - 1,
					}}
					label={t("ledger.pagination_label")}
					actions={{
						goToPrevpage: () => onPage(page - 1, limit),
						goToNextPage: () => onPage(page + 1, limit),
						goToPage: (value) => onPage(Number(value), limit),
						goToFirstPage: () => onPage(1, limit),
						goToLastPage: () => onPage(pages, limit),
						onChangeLimit: (value) => onPage(1, Number(value)),
					}}
				/>
			</div>
		</>
	);
}

function Entry({
	entry,
	last,
}: {
	entry: IncentiveLedgerEntry;
	last: boolean;
}) {
	const { t } = useTranslation("bonus-ro");

	return (
		<li className="grid grid-cols-[2.75rem_0.625rem_minmax(0,1fr)_auto] gap-x-3">
			<time
				dateTime={entry.createdAt}
				className="pt-px text-xs tabular-nums text-muted-foreground"
			>
				{timeFormat.format(new Date(entry.createdAt))}
			</time>
			<div aria-hidden className="flex flex-col items-center">
				<span
					className={cn(
						"mt-1 size-2.5 shrink-0 rounded-full",
						DOT[entry.entryType],
					)}
				/>
				{!last && <span className="mt-1 w-px flex-1 bg-border" />}
			</div>
			<div className={cn("flex min-w-0 flex-col gap-1", !last && "pb-6")}>
				<div className="flex flex-wrap items-baseline gap-x-2">
					<span className="text-sm font-medium">
						{t(`ledger.types.${entry.entryType}`, {
							defaultValue: entry.entryType,
						})}
					</span>
					<span className="text-xs text-muted-foreground">
						{ledgerActor(entry) ?? t("ledger.system")}
					</span>
				</div>
				{entry.reason ? (
					<p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
						{entry.reason}
					</p>
				) : null}
			</div>
			<span
				className={cn(
					"text-sm font-medium tabular-nums text-nowrap",
					entry.amount < 0
						? "text-destructive"
						: "text-emerald-700 dark:text-emerald-400",
				)}
			>
				{formatSigned(entry.amount)}
			</span>
		</li>
	);
}
